import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Users, AlertCircle, Building2, Target, Brain, Copy, Link } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/CompanyContext';
import { trackProfileComparisonTriggered } from '@/utils/amplitudeEvents';
import AdminPageHeader from '@/components/layouts/AdminPageHeader';
import ComparisonDashboard from '@/components/ComparisonDashboard';
import EnhancedTeamAnalysisDashboard from '@/components/EnhancedTeamAnalysisDashboard';
import MultiPersonSelector from '@/components/MultiPersonSelector';
import SearchablePersonSelect from '@/components/SearchablePersonSelect';
import TeamContextSelector from '@/components/TeamContextSelector';
import ManagerSelector from '@/components/ManagerSelector';
import { PositionBuilder } from '@/components/position-analysis/PositionBuilder';
import { PositionAnalysisDashboard } from '@/components/position-analysis/PositionAnalysisDashboard';
import { calculateCompatibility, generateInsights, transformScores, ComparisonData } from '@/utils/comparisonAnalysis';
import { TeamMember, EnhancedTeamAnalysis, TeamAnalysisContext } from '@/types/teamAnalysisTypes';
import { CompanyPosition, PositionMemberAssignment, PositionAnalysisResult } from '@/types/positionAnalysisTypes';
import { generatePositionAnalysis } from '@/utils/positionFitAnalysis';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePagination } from '@/hooks/usePagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ProfileModal from '@/components/ProfileModal';
import { TalentPageSkeleton } from '@/components/skeletons/TalentPageSkeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { generateRegistrationLink, generateWorkBehaviorLink, generateCognitiveAssessmentLink, copyToClipboard } from '@/utils/registrationLinks';

interface SurveySubmission {
  id: string;
  full_name: string;
  email: string;
  profile_name?: string | null;
  scores: any;
  selected_words: any;
  selected_words_text: any;
  completed_at: string;
  created_at: string;
  assessment_type?: string;
  assessment_variant?: string | null;
  assessment_metadata?: any;
  cached_insights?: any;
}

// Deduplicate submissions by email, keeping the newest and merging work behavior history
const deduplicateByEmail = (data: SurveySubmission[]): SurveySubmission[] => {
  const emailMap = new Map<string, SurveySubmission>();
  
  data.forEach(submission => {
    const email = submission.email?.toLowerCase();
    if (!email) return;
    
    const existing = emailMap.get(email);
    const submissionDate = new Date(submission.completed_at || submission.created_at).getTime();
    const existingDate = existing ? new Date(existing.completed_at || existing.created_at).getTime() : 0;
    
    if (!existing || submissionDate > existingDate) {
      // This submission is newer - use it as primary
      const existingHistory = existing 
        ? ((existing.scores as any)?.workBehaviorHistory || [])
        : [];
      
      // If replacing an existing entry, move its work behavior to history
      if (existing && (existing.scores as any)?.workBehavior) {
        existingHistory.push({
          ...(existing.scores as any).workBehavior,
          completedAt: existing.completed_at
        });
      }
      
      emailMap.set(email, {
        ...submission,
        scores: {
          ...(submission.scores as Record<string, any>),
          workBehaviorHistory: existingHistory
        }
      });
    } else {
      // This submission is older - add its work behavior to history
      const existingScores = existing.scores as Record<string, any>;
      const submissionScores = submission.scores as Record<string, any>;
      
      if (submissionScores?.workBehavior) {
        existingScores.workBehaviorHistory = existingScores.workBehaviorHistory || [];
        existingScores.workBehaviorHistory.push({
          ...submissionScores.workBehavior,
          completedAt: submission.completed_at
        });
      }
    }
  });
  
  return Array.from(emailMap.values());
};

const PersonComparison = () => {
  const isInitialLoad = useRef(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [submissions, setSubmissions] = useState<SurveySubmission[]>([]);
  const [allCompanyMembers, setAllCompanyMembers] = useState<SurveySubmission[]>([]);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [person1Id, setPerson1Id] = useState<string>('');
  const [person2Id, setPerson2Id] = useState<string>('');
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);
  const [enhancedTeamAnalysis, setEnhancedTeamAnalysis] = useState<EnhancedTeamAnalysis | null>(null);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [teamContext, setTeamContext] = useState<Partial<TeamAnalysisContext>>({});
  const [selectedManagerId, setSelectedManagerId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [analysisType, setAnalysisType] = useState<'individual' | 'team' | 'company' | 'company2'>('individual');
  
  
  // Pagination
  const {
    currentPage,
    pageSize,
    totalPages,
    startIndex,
    endIndex,
    hasNextPage,
    hasPreviousPage,
    setCurrentPage,
    setPageSize,
    goToNextPage,
    goToPreviousPage,
  } = usePagination({
    totalItems: totalSubmissions,
    initialPageSize: 50,
  });
  
  // Position Analysis State
  const [companyPositions, setCompanyPositions] = useState<CompanyPosition[]>([]);
  const [positionAnalysisResults, setPositionAnalysisResults] = useState<PositionAnalysisResult | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<SurveySubmission | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentCompany } = useCompany();
  const companySlug = currentCompany?.company_slug || null;

  const handleCopyAssessmentLink = async (linkType: 'registration' | 'workBehavior' | 'cognitive') => {
    if (!companySlug) return;
    
    let link = '';
    let linkName = '';
    
    switch (linkType) {
      case 'registration':
        link = generateRegistrationLink(companySlug);
        linkName = 'Full Assessment';
        break;
      case 'workBehavior':
        link = generateWorkBehaviorLink(companySlug);
        linkName = 'Work Behavior Assessment';
        break;
      case 'cognitive':
        link = generateCognitiveAssessmentLink(companySlug);
        linkName = 'Cognitive Assessment';
        break;
    }
    
    const success = await copyToClipboard(link);
    if (success) {
      toast({
        title: "Link Copied!",
        description: `${linkName} link copied to clipboard`
      });
    } else {
      toast({
        title: "Copy Failed",
        description: "Unable to copy link to clipboard",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    if (user) {
      fetchSubmissions();
    }
  }, [user, currentPage, pageSize, currentCompany?.company_id]);


  useEffect(() => {
    if (currentCompany?.company_id) {
      fetchAllCompanyMembers();
    }
  }, [currentCompany?.company_id]);

  // Reset manager selection when team changes
  useEffect(() => {
    if (!selectedTeamIds.includes(selectedManagerId)) {
      setSelectedManagerId('');
    }
  }, [selectedTeamIds, selectedManagerId]);

  // Reset all selections when company changes
  useEffect(() => {
    setPerson1Id('');
    setPerson2Id('');
    setSelectedTeamIds([]);
    setSelectedManagerId('');
    setTeamContext({});
    setCompanyPositions([]);
    setPositionAnalysisResults(null);
    setCurrentPage(1);
  }, [currentCompany?.company_id]);

  // Real-time subscription for deletions
  useEffect(() => {
    if (!currentCompany?.company_id) return;
    
    const channel = supabase
      .channel('talent-submissions-changes')
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'survey_submissions'
        },
        (payload) => {
          console.log('Submission deleted, updating local state:', payload);
          // Remove from local state immediately
          setAllCompanyMembers(prev => prev.filter(m => m.id !== payload.old.id));
          setSubmissions(prev => prev.filter(s => s.id !== payload.old.id));
          setTotalSubmissions(prev => Math.max(0, prev - 1));
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentCompany?.company_id]);

  const refreshCompanyMembers = async () => {
    await fetchAllCompanyMembers();
    await fetchSubmissions();
  };

  const fetchSubmissions = async () => {
    try {
      if (isInitialLoad.current) {
        setInitialLoading(true);
      }

      // Build count query with company filtering
      let countQuery = supabase
        .from('survey_submissions')
        .select('*', { count: 'exact', head: true });

      // Apply company filter
      if (currentCompany?.company_id) {
        countQuery = countQuery.eq('company_id', currentCompany.company_id);
      }

      const { count, error: countError } = await countQuery;
      if (countError) throw countError;
      setTotalSubmissions(count || 0);

      // Calculate range directly inside function to avoid stale state
      const start = (currentPage - 1) * pageSize;
      const end = start + pageSize - 1;

      // Build data query with same filter
      let dataQuery = supabase
        .from('survey_submissions')
        .select('id, full_name, email, scores, selected_words, selected_words_text, completed_at, created_at, profile_name, assessment_type, assessment_variant, assessment_metadata, cached_insights');

      if (currentCompany?.company_id) {
        dataQuery = dataQuery.eq('company_id', currentCompany.company_id);
      }

      const { data, error } = await dataQuery
        .order('completed_at', { ascending: false })
        .range(start, end);

      if (error) throw error;
      
      // Deduplicate by email - keeps newest behavioral data, merges work behavior history
      const deduplicatedData = deduplicateByEmail(data || []);
      console.log('Fetched submissions:', deduplicatedData.length, 'unique emails from', data?.length, 'rows');
      setSubmissions(deduplicatedData);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast({
        title: "Error",
        description: "Failed to load assessment data.",
        variant: "destructive"
      });
    } finally {
      if (isInitialLoad.current) {
        setInitialLoading(false);
        isInitialLoad.current = false;
      }
    }
  };

  const fetchAllCompanyMembers = async () => {
    if (!currentCompany?.company_id) return;
    
    try {
      let query = supabase
        .from('survey_submissions')
        .select('id, full_name, email, scores, selected_words, selected_words_text, completed_at, created_at, profile_name, assessment_type, assessment_variant, assessment_metadata, cached_insights');

      query = query.eq('company_id', currentCompany.company_id);

      const { data, error } = await query.order('full_name', { ascending: true });
      
      if (error) throw error;
      
      // Deduplicate by email for dropdown options - keeps newest per email
      const deduplicatedMembers = deduplicateByEmail(data || []);
      setAllCompanyMembers(deduplicatedMembers);
    } catch (error) {
      console.error('Error fetching all company members:', error);
    }
  };

  const handleCompare = async () => {
    if (!person1Id || !person2Id) {
      toast({
        title: "Selection Required",
        description: "Please select two people to compare.",
        variant: "destructive"
      });
      return;
    }

    if (person1Id === person2Id) {
      toast({
        title: "Invalid Selection",
        description: "Please select two different people to compare.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const person1 = submissions.find(s => s.id === person1Id);
      const person2 = submissions.find(s => s.id === person2Id);

      if (!person1 || !person2) {
        throw new Error('Selected people not found');
      }

      // Transform and validate scores
      const person1Scores = transformScores(person1.scores);
      const person2Scores = transformScores(person2.scores);

      // Calculate compatibility using transformed scores
      const compatibility = calculateCompatibility(person1Scores, person2Scores);
      
      // Generate AI-powered insights (this is now async)
      const insights = await generateInsights(person1, person2, compatibility);

      const comparison: ComparisonData = {
        person1: {
          name: person1.full_name,
          email: person1.email,
          scores: person1Scores,
          selectedWords: Array.isArray(person1.selected_words) ? person1.selected_words : []
        },
        person2: {
          name: person2.full_name,
          email: person2.email,
          scores: person2Scores,
          selectedWords: Array.isArray(person2.selected_words) ? person2.selected_words : []
        },
        compatibility,
        insights
      };

      setComparisonData(comparison);

      // EVENT 14: Profile Comparison Triggered
      trackProfileComparisonTriggered({
        company_id: currentCompany?.company_id || null,
        user_id: user?.id || null,
        person1_name: person1.full_name,
        person1_email: person1.email,
        person1_profile: person1.profile_name || null,
        person2_name: person2.full_name,
        person2_email: person2.email,
        person2_profile: person2.profile_name || null,
        compatibility_score: compatibility?.overall || null,
      });

      // Save comparison to database
      if (user) {
        const { error: insertError } = await supabase
          .from('comparison_sessions')
          .insert({
            user_id: user.id,
            person1_submission_id: person1Id,
            person2_submission_id: person2Id,
            comparison_data: comparison as any
          });

        if (insertError) {
          console.error('Error saving comparison:', insertError);
        }
      }

    } catch (error) {
      console.error('Error generating comparison:', error);
      toast({
        title: "Comparison Failed",
        description: "Unable to generate comparison. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEnhancedTeamAnalysis = async () => {
    if (selectedTeamIds.length < 3 || selectedTeamIds.length > 8) {
      toast({
        title: "Invalid Team Size",
        description: "Please select 3-8 people for team analysis.",
        variant: "destructive"
      });
      return;
    }

    if (!teamContext.teamType || !teamContext.companyPhase || !selectedManagerId) {
      toast({
        title: "Incomplete Context",
        description: "Please select team type, company phase, and manager.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const selectedSubmissions = submissions.filter(s => selectedTeamIds.includes(s.id));
      
      const teamMembers: TeamMember[] = selectedSubmissions.map(submission => ({
        id: submission.id,
        name: submission.full_name,
        email: submission.email,
        scores: transformScores(submission.scores),
        selectedWords: Array.isArray(submission.selected_words) ? submission.selected_words : []
      }));

      // Call the enhanced AI analysis edge function
      const { data: aiAnalysis, error } = await supabase.functions.invoke('analyze-team-context', {
        body: {
          members: teamMembers,
          teamType: teamContext.teamType,
          companyPhase: teamContext.companyPhase,
          managerId: selectedManagerId
        }
      });

      if (error) throw error;

      const enhancedAnalysis: EnhancedTeamAnalysis = {
        members: teamMembers,
        context: { ...teamContext, managerId: selectedManagerId } as TeamAnalysisContext,
        overallTeamScore: aiAnalysis.overallTeamScore,
        managerAnalysis: {
          superpowers: aiAnalysis.managerSuperpowers,
          attentionAreas: aiAnalysis.managerAttentionAreas
        },
        keyInsights: aiAnalysis.keyInsights,
        recommendations: aiAnalysis.recommendations,
        individualRoles: aiAnalysis.individualRoles
      };

      setEnhancedTeamAnalysis(enhancedAnalysis);

      console.log('Enhanced team analysis completed:', enhancedAnalysis);

    } catch (error) {
      console.error('Error generating enhanced team analysis:', error);
      toast({
        title: "Analysis Failed",
        description: "Unable to generate team analysis. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const resetAnalysis = () => {
    setComparisonData(null);
    setEnhancedTeamAnalysis(null);
    setPerson1Id('');
    setPerson2Id('');
    setSelectedTeamIds([]);
    setSelectedManagerId('');
    setTeamContext({});
  };

  // Position Analysis Handlers
  const handlePositionAssignmentChange = (positionId: string, members: PositionMemberAssignment[]) => {
    setCompanyPositions(positions =>
      positions.map(p => p.id === positionId ? { ...p, assignedMembers: members } : p)
    );
  };

  const handleGeneratePositionAnalysis = () => {
    const results = generatePositionAnalysis(companyPositions);
    setPositionAnalysisResults(results);
    toast({
      title: "Analysis Complete",
      description: "Position analysis generated successfully."
    });
  };

  const resetPositionAnalysis = () => {
    setPositionAnalysisResults(null);
  };

  const handleViewMemberProfile = (memberId: string) => {
    const submission = allCompanyMembers.find(s => s.id === memberId);
    if (submission) {
      setSelectedSubmission(submission);
      setIsProfileModalOpen(true);
    }
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
    setSelectedSubmission(null);
  };

  const handleSubmissionUpdate = (updatedSubmission: SurveySubmission) => {
    // Update submissions array
    setSubmissions(prev => 
      prev.map(s => s.id === updatedSubmission.id ? updatedSubmission : s)
    );
    // Update allCompanyMembers array
    setAllCompanyMembers(prev => 
      prev.map(s => s.id === updatedSubmission.id ? updatedSubmission : s)
    );
    // Update selected submission for immediate effect
    setSelectedSubmission(updatedSubmission);
  };

  const hasValidPositionAssignments = companyPositions.some(
    p => p.resolvedProfile !== null && p.assignedMembers.length > 0
  );

  const selectedTeamMembers = submissions
    .filter(s => selectedTeamIds.includes(s.id))
    .map(s => ({ id: s.id, name: s.full_name, email: s.email }));

  const canAnalyze = selectedTeamIds.length >= 3 && 
    selectedTeamIds.length <= 8 && 
    teamContext.teamType && 
    teamContext.companyPhase && 
    selectedManagerId;

  if (comparisonData) {
    return (
      <>
        <AdminPageHeader title="Talent Optimization" />
        <ComparisonDashboard data={comparisonData} onBack={resetAnalysis} />
      </>
    );
  }

  if (enhancedTeamAnalysis) {
    return (
      <>
        <AdminPageHeader title="Talent Optimization" />
        <EnhancedTeamAnalysisDashboard analysis={enhancedTeamAnalysis} onBack={resetAnalysis} />
      </>
    );
  }

  return (
    <>
      <AdminPageHeader title="Talent Optimization" />
      <div className="min-h-screen bg-background p-4">
      <div className="max-w-4xl mx-auto">
        <Card className="border border-border shadow-lg">
          <CardHeader className="text-center pb-6">
            <CardTitle className="flex items-center justify-center gap-3 text-2xl font-bold text-foreground">
              <Users className="h-8 w-8 text-primary" />
              People & Team Analysis
            </CardTitle>
            <p className="text-muted-foreground mt-2">
              Analyze individual compatibility or team dynamics with AI-powered insights
            </p>
          </CardHeader>
          
          <CardContent>
            {initialLoading ? (
              <TalentPageSkeleton />
            ) : submissions.length > 0 ? (
              <>
                <Tabs value={analysisType} onValueChange={(value) => setAnalysisType(value as 'individual' | 'team' | 'company' | 'company2')}>
                  <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="individual" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Individual
                  </TabsTrigger>
                  <TabsTrigger value="team" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Team
                  </TabsTrigger>
                  <TabsTrigger value="company" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Company
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="individual" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SearchablePersonSelect
                      submissions={submissions}
                      value={person1Id}
                      onValueChange={setPerson1Id}
                      placeholder="Select first person..."
                      label="First Person"
                      dotColor="bg-primary"
                    />

                    <SearchablePersonSelect
                      submissions={submissions}
                      value={person2Id}
                      onValueChange={setPerson2Id}
                      placeholder="Select second person..."
                      label="Second Person"
                      dotColor="bg-secondary"
                    />
                  </div>

                  <div className="flex justify-center pt-4 pb-4">
                    <Button
                      onClick={handleCompare}
                      disabled={!person1Id || !person2Id || loading}
                      className="px-8 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
                    >
                      {loading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                          Analyzing...
                        </div>
                      ) : (
                        'Compare Profiles'
                      )}
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="team" className="space-y-6">
                  <TeamContextSelector
                    context={teamContext}
                    onContextChange={setTeamContext}
                    disabled={loading}
                  />

                  <MultiPersonSelector
                    submissions={submissions}
                    selectedIds={selectedTeamIds}
                    onSelectionChange={setSelectedTeamIds}
                    onAnalyze={handleEnhancedTeamAnalysis}
                    loading={loading}
                    showAnalyzeButton={false}
                  />

                  {selectedTeamMembers.length >= 3 && (
                    <ManagerSelector
                      teamMembers={selectedTeamMembers}
                      selectedManagerId={selectedManagerId}
                      onManagerChange={setSelectedManagerId}
                      disabled={loading}
                    />
                  )}

                  {selectedTeamMembers.length >= 3 && (
                    <div className="flex justify-center pt-4">
                      <Button
                        onClick={handleEnhancedTeamAnalysis}
                        disabled={!canAnalyze || loading}
                        className="px-8 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
                      >
                        {loading ? (
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                            Analyzing...
                          </div>
                        ) : (
                          'Generate AI Team Analysis'
                        )}
                      </Button>
                    </div>
                  )}

                  {selectedTeamIds.length > 0 && selectedTeamIds.length < 3 && (
                    <div className="text-center text-sm text-muted-foreground">
                      Select at least 3 team members to enable analysis
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="company" className="space-y-6">
                    {positionAnalysisResults ? (
                      <PositionAnalysisDashboard 
                        results={positionAnalysisResults} 
                        onBack={resetPositionAnalysis}
                        onViewMemberProfile={handleViewMemberProfile}
                      />
                    ) : (
                    <>
                      <PositionBuilder 
                        positions={companyPositions}
                        onPositionsChange={setCompanyPositions}
                        availableMembers={allCompanyMembers}
                        onAssignmentChange={handlePositionAssignmentChange}
                      />
                      
                      {hasValidPositionAssignments && (
                        <div className="flex justify-center pb-6">
                          <Button onClick={handleGeneratePositionAnalysis} size="lg">
                            Generate Position Analysis
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>
                </Tabs>
                
                {/* Pagination Controls */}
                {totalSubmissions > 0 && (
                  <div className="flex items-center justify-between pt-6 border-t border-border">
                    <div className="flex items-center gap-4">
                      <p className="text-sm text-muted-foreground">
                        Showing {startIndex + 1}-{Math.min(endIndex, totalSubmissions)} of {totalSubmissions} submissions
                      </p>
                      <Select value={pageSize.toString()} onValueChange={(value) => setPageSize(Number(value))}>
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="25">25 per page</SelectItem>
                          <SelectItem value="50">50 per page</SelectItem>
                          <SelectItem value="100">100 per page</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToPreviousPage}
                        disabled={!hasPreviousPage}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-muted-foreground">
                          Page {currentPage} of {totalPages}
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToNextPage}
                        disabled={!hasNextPage}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2 text-foreground">No Assessment Data Available</h3>
                <p className="text-sm mb-4">Complete some behavioral assessments first to use this feature.</p>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="text-primary border-border hover:bg-muted gap-2"
                    >
                      <Link className="h-4 w-4" />
                      Share Links
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-80 bg-background border shadow-lg z-50">
                    <DropdownMenuItem 
                      onClick={() => handleCopyAssessmentLink('registration')}
                      className="flex items-center justify-between cursor-pointer bg-primary/5 border-l-2 border-primary"
                    >
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                        <span className="font-medium">Full Assessment</span>
                        <Badge variant="secondary" className="text-xs px-1.5 py-0 flex-shrink-0">Recommended</Badge>
                      </div>
                      <Copy className="h-4 w-4 flex-shrink-0" />
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem 
                      onClick={() => handleCopyAssessmentLink('workBehavior')}
                      className="flex items-center justify-between cursor-pointer"
                    >
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <Target className="h-4 w-4 flex-shrink-0" />
                        <span>Work Behavior Only</span>
                      </div>
                      <Copy className="h-4 w-4 flex-shrink-0" />
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem 
                      onClick={() => handleCopyAssessmentLink('cognitive')}
                      className="flex items-center justify-between cursor-pointer"
                    >
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <Brain className="h-4 w-4 flex-shrink-0" />
                        <span>Cognitive Only</span>
                      </div>
                      <Copy className="h-4 w-4 flex-shrink-0" />
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>

    <ProfileModal
      submission={selectedSubmission}
      isOpen={isProfileModalOpen}
      onClose={handleCloseProfileModal}
      onSubmissionUpdate={handleSubmissionUpdate}
    />
    </>
  );
};

export default PersonComparison;
